import { useCreditsResponseEvent } from "../events/useCreditsResponseEvent";
import { handleAjaxError } from "../actions/handleAjaxError";
import { displayModal } from "../actions/displayModal";
import { loadModal } from "../actions/loadModal";
import { updateCredits } from "../actions/updateCredits";
import { imageWrapperIconImgToVideo } from "./imageWrapperIconImgToVideo";
import { ensureContentGeneratingLoader } from "../actions/appendUserMessage";
import { syncContentGeneratorPoseThumbnails } from "./contentGeneratorPoseThumbnails";
import { filterSettingsVoicesByProvider } from "../actions/playMessage";
import {
  bindVoicePreviewHandlers,
  bindVoiceSelectPreviewHandlers,
} from "./chatSettingsVoice";
import {
  humanizeAccessRequirementMessage,
  isAccessRequirementError,
} from "../helpers/accessRequirementErrors";

let contentGeneratorInitialized = false;
export const contentGeneratorModalSelector =
  "#growtypeChatContentGeneratorModal";
export const contentGeneratorReason = "generate_custom_assistant_image";
const contentGeneratorMiniStatusSelector =
  "#growtypeChatContentGeneratorMiniStatus";
const contentGeneratorRunStorageKey = "growtype_chat_content_generation_run";
const contentGeneratorUrlParam = "generate_media";
const contentGeneratorResultsUrlParam = "generated_media";
let contentGeneratorRunPollInterval = null;
let contentGeneratorRunPollInFlight = false;
let contentGeneratorRunNotFoundCount = 0;

function contentGeneratorRequestedByUrl() {
  const params = new URL(window.location.href).searchParams;

  return (
    params.get(contentGeneratorUrlParam) === "1" ||
    params.get(contentGeneratorResultsUrlParam) === "1"
  );
}

function contentGeneratorResultsRequestedByUrl() {
  return (
    new URL(window.location.href).searchParams.get(
      contentGeneratorResultsUrlParam
    ) === "1"
  );
}

function setContentGeneratorUrlState(isOpen) {
  if (!window.history?.replaceState) return;

  const url = new URL(window.location.href);

  if (isOpen) {
    url.searchParams.set(contentGeneratorUrlParam, "1");
  } else {
    url.searchParams.delete(contentGeneratorUrlParam);
    url.searchParams.delete(contentGeneratorResultsUrlParam);
  }

  window.history.replaceState(window.history.state, "", url.toString());
}

function setContentGeneratorResultsUrlState(isOpen) {
  if (!window.history?.replaceState) return;

  const url = new URL(window.location.href);

  if (isOpen) {
    url.searchParams.set(contentGeneratorUrlParam, "1");
    url.searchParams.set(contentGeneratorResultsUrlParam, "1");
  } else {
    url.searchParams.delete(contentGeneratorResultsUrlParam);
  }

  window.history.replaceState(window.history.state, "", url.toString());
}

function openContentGeneratorRequestedByUrl() {
  if (!contentGeneratorRequestedByUrl()) return;

  const openFromTrigger = () => {
    const trigger = document.querySelector(".btn-content-generator-trigger");

    if (!trigger) return false;

    trigger.click();
    return true;
  };

  if (openFromTrigger()) return;

  const observer = new MutationObserver(() => {
    if (openFromTrigger()) {
      observer.disconnect();
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.setTimeout(() => observer.disconnect(), 10000);
}

function readPersistedContentGeneratorRun() {
  try {
    const value = window.sessionStorage?.getItem(contentGeneratorRunStorageKey);
    const run = value ? JSON.parse(value) : null;

    return run && run.trackingId ? run : null;
  } catch (error) {
    return null;
  }
}

function writePersistedContentGeneratorRun(run) {
  try {
    window.sessionStorage?.setItem(
      contentGeneratorRunStorageKey,
      JSON.stringify(run)
    );
  } catch (error) {
    // Cross-page status is optional when storage is unavailable.
  }
}

function clearPersistedContentGeneratorRun() {
  try {
    window.sessionStorage?.removeItem(contentGeneratorRunStorageKey);
  } catch (error) {
    // Cross-page status is optional when storage is unavailable.
  }
}

function createContentGeneratorTrackingId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `generation_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}

function contentGeneratorTypeLabelFromValue(contentType) {
  if (contentType === "audio") return "audio";
  if (contentType === "video") return "video";
  if (contentType === "image") return "image";
  return "media";
}

const contentGeneratorProgressTimeline = Object.freeze([
  { durationMs: 5000, targetPercent: 65 },
  { durationMs: 5000, targetPercent: 75 },
  { durationMs: 5000, targetPercent: 80 },
  { durationMs: 5000, targetPercent: 85 },
  { durationMs: 5000, targetPercent: 90 },
  { durationMs: 10000, targetPercent: 95 },
]);
const contentGeneratorWaitingPercent = 99;
const contentGeneratorTimelineDurationMs = contentGeneratorProgressTimeline.reduce(
  (total, phase) => total + phase.durationMs,
  0
);

export function calculateContentGeneratorProgress(elapsedMs) {
  const boundedElapsedMs = Math.max(0, Number(elapsedMs) || 0);
  let phaseStartedAtMs = 0;
  let phaseStartedAtPercent = 0;

  for (const phase of contentGeneratorProgressTimeline) {
    const phaseEndsAtMs = phaseStartedAtMs + phase.durationMs;
    if (boundedElapsedMs <= phaseEndsAtMs) {
      const phaseRatio = Math.min(
        (boundedElapsedMs - phaseStartedAtMs) / phase.durationMs,
        1
      );
      return (
        phaseStartedAtPercent +
        (phase.targetPercent - phaseStartedAtPercent) * phaseRatio
      );
    }

    phaseStartedAtMs = phaseEndsAtMs;
    phaseStartedAtPercent = phase.targetPercent;
  }

  return contentGeneratorWaitingPercent;
}

function setContentGeneratorMiniProgress(percent) {
  const miniStatus = ensureContentGeneratorMiniStatus();
  const boundedPercent = Math.max(0, Math.min(Number(percent) || 0, 100));
  const miniCircumference = 107;
  const miniOffset =
    miniCircumference - (boundedPercent / 100) * miniCircumference;

  miniStatus
    .find(".content-generator-mini-progress-bar")
    .css("stroke-dashoffset", miniOffset);
  miniStatus
    .find(".content-generator-mini-progress-value")
    .text(`${Math.round(boundedPercent)}%`);
}

function renderPersistedContentGeneratorRun(run) {
  if (!run) return;

  const miniStatus = ensureContentGeneratorMiniStatus();
  const modal = $(contentGeneratorModalSelector);
  const state = run.state ?? "generating";
  const contentType = contentGeneratorTypeLabelFromValue(run.contentType);
  const runMessage = humanizeAccessRequirementMessage(run.message);
  const title =
    state === "ready"
      ? "Your media is ready"
      : state === "error"
        ? "Generation stopped"
        : `Generating ${contentType}`;
  const description =
    runMessage ||
    (state === "ready"
      ? "Open to view the result."
      : state === "error"
        ? "Generation failed. Please try again."
        : "Generation continues while you browse.");

  miniStatus
    .data("dismissed", Boolean(run.dismissed))
    .removeClass("is-generating is-ready is-error")
    .addClass(`is-${state}`)
    .find(".content-generator-mini-title")
    .text(title);
  miniStatus.find(".content-generator-mini-description").text(description);

  setContentGeneratorMiniProgress(run.percent ?? (state === "ready" ? 100 : 12));

  if (isContentGeneratorVisible(modal)) {
    setContentGeneratorRunState(modal, state, runMessage);
    if (state === "generating") {
      showContentGeneratorProgress(modal, run.percent ?? 12);
    } else {
      stopContentGeneratorProgressSimulation(modal);
      modal.find(".content-generator-submit").attr("disabled", false);
    }
    miniStatus.addClass("d-none").attr("aria-hidden", "true");
    return;
  }

  miniStatus
    .toggleClass("d-none", Boolean(run.dismissed))
    .attr("aria-hidden", run.dismissed ? "true" : "false");
}

function persistContentGeneratorRun(params) {
  const run = {
    trackingId: createContentGeneratorTrackingId(),
    contentType: params.content_type ?? "media",
    startedAt: Date.now(),
    state: "generating",
    percent: 0,
    dismissed: false,
    message: "",
    characterSlug: params.character_slug ?? "",
    roleplaySlug: params.roleplay_scenario ?? "",
  };

  params.generation_tracking_id = run.trackingId;
  writePersistedContentGeneratorRun(run);

  return run;
}

function updatePersistedContentGeneratorRun(state, message = "", percent = null) {
  const run = readPersistedContentGeneratorRun();
  if (!run) return;

  run.state = state;
  run.message = humanizeAccessRequirementMessage(message);
  if (percent !== null) {
    run.percent = percent;
  }
  writePersistedContentGeneratorRun(run);
}

function stopPersistedContentGeneratorPolling() {
  if (contentGeneratorRunPollInterval) {
    clearInterval(contentGeneratorRunPollInterval);
    contentGeneratorRunPollInterval = null;
  }

  contentGeneratorRunNotFoundCount = 0;
}

function failPersistedContentGeneratorRun(run, message) {
  const currentRun = readPersistedContentGeneratorRun();
  if (!currentRun || currentRun.trackingId !== run.trackingId) {
    return;
  }

  run.state = "error";
  run.message = humanizeAccessRequirementMessage(message);
  writePersistedContentGeneratorRun(run);
  renderPersistedContentGeneratorRun(run);
  stopPersistedContentGeneratorPolling();
}

function pollPersistedContentGeneratorRun() {
  const run = readPersistedContentGeneratorRun();
  const action = window.growtype_chat_ajax?.content_generation_status_action;

  if (!run || run.state !== "generating" || !action) {
    stopPersistedContentGeneratorPolling();
    return;
  }

  if (contentGeneratorRunPollInFlight) {
    return;
  }

  contentGeneratorRunPollInFlight = true;

  $.ajax({
    url: window.growtype_chat_ajax.url,
    type: "post",
    data: {
      action: action,
      nonce: window.growtype_chat_ajax.nonce,
      tracking_id: run.trackingId,
    },
    success: function (response) {
      if (!response.success || !response.data) return;
      if (response.data.tracking_id !== run.trackingId) return;

      contentGeneratorRunNotFoundCount = 0;

      const status = response.data.status;
      if (status === "completed") {
        run.state = "ready";
        run.percent = 100;
        run.message = "";
        writePersistedContentGeneratorRun(run);
        renderPersistedContentGeneratorRun(run);
        stopPersistedContentGeneratorPolling();
        return;
      }

      if (status === "failed" || status === "error") {
        run.state = "error";
        run.message = humanizeAccessRequirementMessage(
          response.data.message || "Generation failed. Please try again."
        );
        writePersistedContentGeneratorRun(run);
        renderPersistedContentGeneratorRun(run);
        stopPersistedContentGeneratorPolling();
        return;
      }

      run.percent = calculateContentGeneratorProgress(
        Date.now() - Number(run.startedAt || 0)
      );
      writePersistedContentGeneratorRun(run);
      renderPersistedContentGeneratorRun(run);
    },
    error: function (response) {
      // The next page can arrive before the generation request has written its
      // server-side tracker. Allow a bounded grace period for that race.
      if (response.status === 404) {
        contentGeneratorRunNotFoundCount += 1;
        const elapsed = Date.now() - Number(run.startedAt || 0);

        if (contentGeneratorRunNotFoundCount >= 10 && elapsed > 30000) {
          failPersistedContentGeneratorRun(
            run,
            "Generation status is unavailable. Please try again."
          );
        }
        return;
      }

      if (response.status === 401 || response.status === 403) {
        failPersistedContentGeneratorRun(
          run,
          "Please sign in again to check generation status."
        );
      }
    },
    complete: function () {
      contentGeneratorRunPollInFlight = false;
    },
  });
}

function startPersistedContentGeneratorPolling() {
  stopPersistedContentGeneratorPolling();
  pollPersistedContentGeneratorRun();
  contentGeneratorRunPollInterval = setInterval(
    pollPersistedContentGeneratorRun,
    3000
  );
}

function restorePersistedContentGeneratorRun() {
  const run = readPersistedContentGeneratorRun();
  if (!run) return;

  if (Date.now() - Number(run.startedAt || 0) > 20 * 60 * 1000) {
    clearPersistedContentGeneratorRun();
    return;
  }

  if (run.state === "generating") {
    run.percent = calculateContentGeneratorProgress(
      Date.now() - Number(run.startedAt || 0)
    );
    writePersistedContentGeneratorRun(run);
  }

  renderPersistedContentGeneratorRun(run);

  if (run.state === "generating") {
    startPersistedContentGeneratorPolling();
  }
}

function ensureContentGeneratorMiniStatus() {
  let miniStatus = $(contentGeneratorMiniStatusSelector);

  if (miniStatus.length) {
    return miniStatus;
  }

  miniStatus = $(`
    <aside id="growtypeChatContentGeneratorMiniStatus"
      class="content-generator-mini-status d-none"
      aria-live="polite"
      aria-atomic="true">
      <button type="button" class="content-generator-mini-restore" aria-label="Open generation progress">
        <span class="content-generator-mini-progress" aria-hidden="true">
          <svg viewBox="0 0 40 40">
            <circle class="content-generator-mini-progress-bg" cx="20" cy="20" r="17"></circle>
            <circle class="content-generator-mini-progress-bar" cx="20" cy="20" r="17"></circle>
          </svg>
          <span class="content-generator-mini-progress-value">0%</span>
          <span class="content-generator-mini-ready-icon">&#10003;</span>
          <span class="content-generator-mini-error-icon">!</span>
        </span>
        <span class="content-generator-mini-copy">
          <strong class="content-generator-mini-title">Generating media</strong>
          <span class="content-generator-mini-description">You can keep browsing.</span>
        </span>
      </button>
      <button type="button" class="content-generator-mini-close" aria-label="Dismiss generation status"></button>
    </aside>
  `).appendTo("body");

  return miniStatus;
}

function showPersistedContentGeneratorRunInModal(modal, run) {
  if (!modal?.length || !run) return;

  const runMessage = humanizeAccessRequirementMessage(run.message);

  if (run.state === "generating") {
    modal.find(".content-generator-submit").attr("disabled", true);
    setContentGeneratorRunState(modal, "generating", runMessage);
    showContentGeneratorProgress(modal, run.percent ?? 12);
  } else if (run.state === "error") {
    modal
      .find(".content-generator-modal-error")
      .removeClass("d-none")
      .text(runMessage || "Generation failed. Please try again.");
    setContentGeneratorRunState(modal, "error", runMessage);
  }

  ensureContentGeneratorMiniStatus()
    .addClass("d-none")
    .attr("aria-hidden", "true");
  window.growtypeModal?.show(modal[0]);
}

function contentGeneratorReadyUrl(run) {
  const characterSlug = String(run?.characterSlug || "").trim();
  const roleplaySlug = String(run?.roleplaySlug || "").trim();

  if (roleplaySlug && roleplaySlug !== "none") {
    return `/roleplays/content/${encodeURIComponent(roleplaySlug)}/`;
  }

  return characterSlug
    ? `/character/profile/${encodeURIComponent(characterSlug)}?${contentGeneratorUrlParam}=1&${contentGeneratorResultsUrlParam}=1`
    : "/assistants/content/";
}

function openPersistedContentGeneratorRun() {
  const run = readPersistedContentGeneratorRun();
  if (!run) return;

  if (run.state === "ready") {
    clearPersistedContentGeneratorRun();
    ensureContentGeneratorMiniStatus()
      .data("dismissed", true)
      .addClass("d-none")
      .attr("aria-hidden", "true");
    window.location.href = contentGeneratorReadyUrl(run);
    return;
  }

  const modal = $(contentGeneratorModalSelector);
  if (modal.length) {
    showPersistedContentGeneratorRunInModal(modal, run);
    return;
  }

  loadModal(window.growtype_chat_ajax?.get_content_generator_modal_action, null, {
    modalId: "growtypeChatContentGeneratorModal",
    ajaxData: {
      character_slug: run.characterSlug || "",
    },
  })
    .then(function ($modal) {
      showPersistedContentGeneratorRunInModal($modal, run);
    })
    .catch(function () {
      ensureContentGeneratorMiniStatus()
        .find(".content-generator-mini-description")
        .text("Unable to open generation progress. Please try again.");
    });
}

function isContentGeneratorVisible(modal) {
  return modal.length && modal.hasClass("show");
}

function contentGeneratorTypeLabel(modal) {
  const contentType =
    modal.find(".content-generator-nav-item.is-active").data("type") ??
    "media";

  return contentGeneratorTypeLabelFromValue(contentType);
}

function setContentGeneratorRunState(modal, state, message = "") {
  const miniStatus = ensureContentGeneratorMiniStatus();
  const isGenerating = state === "generating";
  const title =
    state === "ready"
      ? "Your media is ready"
      : state === "error"
        ? "Generation stopped"
        : `Generating ${contentGeneratorTypeLabel(modal)}`;
  const displayMessage = humanizeAccessRequirementMessage(message);
  const description =
    displayMessage ||
    (state === "ready"
      ? "Open to view the result."
      : state === "error"
        ? "Open to review the error."
        : "You can keep browsing.");

  modal.data("generationInProgress", isGenerating);
  modal.attr("data-generation-state", state);
  modal.find(".content-generator-minimize").toggleClass("d-none", !isGenerating);

  miniStatus
    .removeClass("is-generating is-ready is-error")
    .addClass(`is-${state}`)
    .find(".content-generator-mini-title")
    .text(title);
  miniStatus
    .find(".content-generator-mini-description")
    .text(description);

  if (isContentGeneratorVisible(modal)) {
    miniStatus.addClass("d-none").attr("aria-hidden", "true");
  } else if (!miniStatus.data("dismissed")) {
    miniStatus.removeClass("d-none").attr("aria-hidden", "false");
  }
}

function minimizeContentGenerator(modal) {
  if (!modal.data("generationInProgress")) {
    return;
  }

  const miniStatus = ensureContentGeneratorMiniStatus();
  miniStatus.data("dismissed", false);
  const run = readPersistedContentGeneratorRun();
  if (run) {
    run.dismissed = false;
    writePersistedContentGeneratorRun(run);
  }
  setContentGeneratorRunState(modal, "generating");
  window.growtypeModal?.hide(modal[0]);
}

function finishContentGeneratorRun(modal, state, message = "") {
  const displayMessage = humanizeAccessRequirementMessage(message);

  updatePersistedContentGeneratorRun(
    state,
    displayMessage,
    state === "ready" ? 100 : null
  );
  stopPersistedContentGeneratorPolling();
  setContentGeneratorRunState(modal, state, displayMessage);
}

function updateProfileGallery(responseData) {
  const generatedData = responseData?.generated_content ?? {};
  const profileHtml = responseData?.profile_html ?? generatedData.profile_html;
  const profileSectionHtml =
    responseData?.profile_section_html ?? generatedData.profile_section_html;
  const tabId = responseData?.profile_tab_id ?? generatedData.profile_tab_id;

  if (!profileHtml || !tabId) {
    return;
  }

  const sectionWasInserted = ensurePrivateSectionExists(
    profileSectionHtml,
    tabId
  );

  const tabPane = $(`#${tabId}`);
  const tabBtn = $(`#${tabId}-tab`);

  if (!tabPane.length) {
    return;
  }

  tabPane.find(".empty-content-placeholder").remove();

  let gallery = tabPane.find(".custom-gallery");
  if (!gallery.length) {
    const galleryClass = tabId.includes("audio")
      ? "custom-gallery audio-gallery"
      : "custom-gallery";
    tabPane.append(`<div class="${galleryClass}"></div>`);
    gallery = tabPane.find(".custom-gallery");
  }

  let $profileHtml = $();
  let insertedCount = 0;

  if (!sectionWasInserted || gallery.children().length === 0) {
    $profileHtml = $(profileHtml);
    const knownGenerationIds = new Set();
    gallery.find("[data-generation-id]").each(function () {
      const generationId = String($(this).attr("data-generation-id") ?? "");
      if (generationId) {
        knownGenerationIds.add(generationId);
      }
    });
    $profileHtml = $profileHtml.filter(function () {
      const generationId = String(
        $(this).find("[data-generation-id]").first().attr("data-generation-id") ?? ""
      );
      if (!generationId) {
        return true;
      }
      if (knownGenerationIds.has(generationId)) {
        return false;
      }
      knownGenerationIds.add(generationId);
      return true;
    });
    insertedCount = $profileHtml.filter(
      ".custom-gallery-image, .custom-gallery-audio"
    ).length;
    gallery.prepend($profileHtml);
  }

  if (
    $profileHtml.length &&
    $profileHtml.find(".lazy-load").length > 0 &&
    typeof $.fn.Lazy === "function"
  ) {
    $profileHtml.find(".lazy-load").Lazy();
  }

  if ($profileHtml.length) {
    imageWrapperIconImgToVideo($profileHtml);
  } else {
    imageWrapperIconImgToVideo(tabPane);
  }

  const amountSpan = tabBtn.find(".amount");
  if (amountSpan.length && !sectionWasInserted && insertedCount > 0) {
    const currentAmount =
      parseInt(amountSpan.text().replace(/[^0-9]/g, ""), 10) || 0;
    amountSpan.text(`(${currentAmount + insertedCount})`);
  }

  if (tabBtn.length && window.bootstrap?.Tab) {
    window.bootstrap.Tab.getOrCreateInstance(tabBtn[0]).show();
  }

  window.lazyloadInstance?.update();
  document.dispatchEvent(
    new CustomEvent("growtypeChatContentGeneratorProfileUpdated")
  );
}

function ensurePrivateSectionExists(profileSectionHtml, tabId) {
  if ($("#private[data-type='private']").length || !profileSectionHtml) {
    return false;
  }

  const $privateSection = $(profileSectionHtml);
  const $premiumSection = $("#premium[data-type='premium']");
  const $publicSection = $("#public[data-type='public']");
  const $mainContent = $(".main-content.container").first();

  if ($premiumSection.length) {
    $premiumSection.before($privateSection);
  } else if ($publicSection.length) {
    $publicSection.after($privateSection);
  } else if ($mainContent.length) {
    $mainContent.append($privateSection);
  }

  const $tabBtn = $(`#${tabId}-tab`);
  if ($tabBtn.length) {
    $tabBtn.removeClass("active").attr("aria-selected", "false");
  }

  return true;
}

export function bindContentGeneratorModal() {
  // We use a NATIVE listener with useCapture: true to intercept the click
  // BEFORE Bootstrap's data-api listener picks it up.
  // Bootstrap attaches to the document level, so we must be earlier in the chain.
  document.addEventListener(
    "click",
    function (event) {
      const closeTrigger = event.target.closest(
        `${contentGeneratorModalSelector} .content-generator-close`
      );

      if (closeTrigger) {
        const modal = $(closeTrigger).closest(contentGeneratorModalSelector);

        if (modal.data("generationInProgress")) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
          minimizeContentGenerator(modal);
          return;
        }
      }

      const trigger = event.target.closest(`.btn-content-generator-trigger`);

      if (trigger) {
        const params = $(trigger).attr("data-params") ?? "{}";
        const initialTriggerName =
          $(trigger).attr("data-initial-trigger-name") ?? "";
        let paramsDecoded = {};

        try {
          paramsDecoded = JSON.parse(params);
        } catch (e) {
          paramsDecoded = {};
        }

        if (!document.querySelector(contentGeneratorModalSelector)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();
          openContentGeneratorModal(
            paramsDecoded,
            $(trigger),
            initialTriggerName
          );
        } else {
          // Modal already exists, just open it with new params
          event.preventDefault();
          openContentGeneratorModal(
            paramsDecoded,
            $(trigger),
            initialTriggerName
          );
        }
      }
    },
    true
  );

  initContentGeneratorModal(
    contentGeneratorModalSelector,
    contentGeneratorReason
  );
  restorePersistedContentGeneratorRun();
  openContentGeneratorRequestedByUrl();
}

export function openContentGeneratorModal(
  paramsDecoded,
  trigger,
  initialTriggerName
) {
  const contentGeneratorModal = $(contentGeneratorModalSelector);
  const activeCharacterSlug = paramsDecoded.character_slug ?? "";
  const renderedCharacterSlug =
    contentGeneratorModal.attr("data-character-slug") ?? "";
  const shouldReloadModal =
    contentGeneratorModal.length &&
    activeCharacterSlug !== "" &&
    renderedCharacterSlug !== activeCharacterSlug;

  if (contentGeneratorModal.length && !shouldReloadModal) {
    setupAndShowContentGeneratorModal(
      contentGeneratorModal,
      paramsDecoded,
      initialTriggerName
    );
  } else {
    if (trigger) {
      trigger.attr("disabled", true);
    }

    loadModal(growtype_chat_ajax.get_content_generator_modal_action, null, {
      modalId: "growtypeChatContentGeneratorModal",
      replace: shouldReloadModal,
      ajaxData: {
        character_slug: activeCharacterSlug,
      },
    })
      .then(function ($newModal) {
        setupAndShowContentGeneratorModal(
          $newModal,
          paramsDecoded,
          initialTriggerName
        );
      })
      .catch(function (err) {
        console.error(err);
      })
      .finally(function () {
        if (trigger) {
          trigger.attr("disabled", false);
        }
      });
  }
}

function setupAndShowContentGeneratorModal(
  modal,
  paramsDecoded,
  initialTriggerName
) {
  const showResultsOnOpen = contentGeneratorResultsRequestedByUrl();
  const submitBtn = modal.find(".content-generator-submit");
  const imagesCountOption = modal.find(
    ".content-generator-count-option.is-active"
  );
  const imagesAmount = imagesCountOption.length
    ? imagesCountOption.data("value")
    : 1;

  modal
    .attr("data-params", JSON.stringify(paramsDecoded))
    .attr("data-initial-trigger-name", initialTriggerName ?? "");

  submitBtn
    .attr("data-character-slug", paramsDecoded.character_slug ?? "")
    .attr("data-character-hash", paramsDecoded.character_hash ?? "")
    .attr("data-character-id", paramsDecoded.character_id ?? "")
    .attr("data-reason", paramsDecoded.reason ?? contentGeneratorReason)
    .attr("data-credits-amount", paramsDecoded.credits_amount ?? "")
    .attr(
      "data-cta-label",
      paramsDecoded.cta_label ?? submitBtn.attr("data-cta-label") ?? ""
    )
    .attr("data-images-amount", imagesAmount);

  initContentGeneratorModal(
    contentGeneratorModalSelector,
    contentGeneratorReason
  );
  initializeContentGeneratorVoiceControl(modal);
  syncContentGeneratorPoseThumbnails(modal);
  updateContentGeneratorCredits(modal);
  showContentGeneratorForm(modal);
  modal.data("showResultsOnOpen", showResultsOnOpen);
  window.growtypeModal?.show(document.querySelector(contentGeneratorModalSelector));
}

function hasContentGeneratorResults(modal) {
  return modal.find(".content-generator-results-list").children().length > 0;
}

function ensureContentGeneratorResultsSection(resultsList, type) {
  let section = resultsList
    .find(`.content-generator-results-section[data-type="${type}"]`)
    .first();

  if (section.length) {
    return section;
  }

  const labels = {
    images: resultsList.data("imagesLabel") ?? "Images",
    videos: resultsList.data("videosLabel") ?? "Videos",
    audio: resultsList.data("audioLabel") ?? "Audio",
  };
  const sectionOrder = ["images", "videos", "audio"];

  section = $(
    '<section class="content-generator-results-section">' +
      '<div class="content-generator-results-section-title">' +
      '<span class="content-generator-results-section-label"></span>' +
      '<span class="content-generator-results-section-count">0</span>' +
      "</div>" +
      '<div class="row g-2"></div>' +
      "</section>"
  ).attr("data-type", type);
  section
    .find(".content-generator-results-section-label")
    .text(labels[type] ?? type);

  const typeIndex = sectionOrder.indexOf(type);
  const nextSection = resultsList
    .find(".content-generator-results-section")
    .filter(function () {
      return sectionOrder.indexOf($(this).attr("data-type")) > typeIndex;
    })
    .first();

  if (nextSection.length) {
    nextSection.before(section);
  } else {
    resultsList.append(section);
  }

  return section;
}

function prependContentGeneratorResults(resultsList, type, columnsHtml) {
  if (!columnsHtml) return;

  const section = ensureContentGeneratorResultsSection(resultsList, type);
  section.find(".row").first().prepend(columnsHtml);
  section
    .find(".content-generator-results-section-count")
    .text(section.find(".content-generator-result-item").length);
}

function applyContentGeneratorResultsFilter(modal, requestedType) {
  const filters = modal.find(".content-generator-results-filter");
  const sections = modal.find(".content-generator-results-section");
  const resultsList = modal.find(".content-generator-results-list");
  const emptyState = modal.find(".content-generator-results-empty");
  const availableTypes = ["all", "images", "videos", "audio"];
  const type = availableTypes.includes(requestedType) ? requestedType : "all";

  filters
    .removeClass("is-active")
    .attr("aria-pressed", "false")
    .filter(`[data-type="${type}"]`)
    .addClass("is-active")
    .attr("aria-pressed", "true");

  sections.each(function () {
    const section = $(this);
    section.toggleClass(
      "d-none",
      type !== "all" && section.attr("data-type") !== type
    );
  });

  const visibleResultCount = sections
    .not(".d-none")
    .find(".content-generator-result-item").length;
  const allResultCount = sections.find(".content-generator-result-item").length;

  filters.each(function () {
    const filter = $(this);
    const filterType = filter.attr("data-type");
    const count =
      filterType === "all"
        ? allResultCount
        : sections
            .filter(`[data-type="${filterType}"]`)
            .find(".content-generator-result-item").length;

    filter.find(".content-generator-results-filter-count").text(count);
  });

  resultsList.toggleClass("d-none", visibleResultCount === 0);
  emptyState
    .toggleClass("d-none", visibleResultCount > 0)
    .text(
      type === "all"
        ? emptyState.attr("data-empty-all")
        : emptyState.attr("data-empty-filtered")
    );
}

export function showContentGeneratorForm(modal) {
  setContentGeneratorResultsUrlState(false);
  modal.find(".content-generator-results-container").addClass("d-none");
  modal.find(".content-generator-form-container").removeClass("d-none");
  modal.find(".content-generator-title").removeClass("d-none");
  modal.find(".content-generator-results-back").addClass("d-none");
  modal.find(".content-generator-results-trigger").removeClass("d-none");

  stopAllAudio(modal);
}

function showContentGeneratorResults(modal) {
  setContentGeneratorResultsUrlState(true);
  const resultsList = modal.find(".content-generator-results-list");
  const activeFilter =
    modal.find(".content-generator-results-filter.is-active").data("type") ??
    "all";

  modal.find(".content-generator-form-container").addClass("d-none");
  modal.find(".content-generator-results-container").removeClass("d-none");
  modal.find(".content-generator-title").addClass("d-none");
  modal.find(".content-generator-results-back").removeClass("d-none");
  modal.find(".content-generator-results-trigger").addClass("d-none");
  applyContentGeneratorResultsFilter(modal, activeFilter);

  if (hasContentGeneratorResults(modal)) {
    if (
      resultsList.find(".lazy-load").length > 0 &&
      typeof $.fn.Lazy === "function"
    ) {
      resultsList.find(".lazy-load").Lazy();
    }
    imageWrapperIconImgToVideo(resultsList);
  }
}

function initializeContentGeneratorVoiceControl(modal) {
  const $voiceSelect = modal.find(
    'select[name="content_generator_tts_model_used"]'
  );

  if (!$voiceSelect.length) {
    return;
  }

  const currentChatVoice = $('.chat-settings select[name="tts_model_used"]')
    .first()
    .val();

  if (
    currentChatVoice &&
    $voiceSelect.find(`option[value="${currentChatVoice}"]`).length
  ) {
    $voiceSelect.val(currentChatVoice);
  }

  if ($.fn.chosen && !$voiceSelect.data("chosen")) {
    $voiceSelect.chosen({
      disable_search_threshold: 30,
      width: "100%",
    });
  }

  filterSettingsVoicesByProvider($voiceSelect);
  syncContentGeneratorVoicePreview($voiceSelect);
  bindVoicePreviewHandlers();
  bindVoiceSelectPreviewHandlers();
}

function syncContentGeneratorVoicePreview($voiceSelect) {
  const previewUrl = $voiceSelect
    .find("option:selected")
    .attr("data-preview-url");

  $voiceSelect
    .closest('.chat-settings-single[data-type="voice"]')
    .find(".btn-preview-voice")
    .attr("data-preview-url", previewUrl || "");
}

export function initContentGeneratorModal(
  modalSelector,
  contentGeneratorReason
) {
  if (contentGeneratorInitialized) {
    return;
  }

  contentGeneratorInitialized = true;

  $(document).off("click.growtypeChatContentGenerator");

  $(document).on(
    "change.growtypeChatContentGenerator",
    `${modalSelector} input[name="nsfw_chat"]`,
    function () {
      syncContentGeneratorPoseThumbnails(
        $(this).closest(".content-generator-settings-inner"),
        this.checked
      );
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-results-filter`,
    function (event) {
      event.preventDefault();
      applyContentGeneratorResultsFilter(
        $(this).closest(modalSelector),
        $(this).data("type")
      );
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-nav-item`,
    function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const type = $(this).data("type");
      const modal = $(this).closest(modalSelector);

      modal.find(".content-generator-nav-item").removeClass("is-active");
      $(this).addClass("is-active");

      modal
        .find(".content-generator-pose-options-single")
        .removeClass("is-active")
        .filter(`[data-type="${type}"]`)
        .addClass("is-active");

      // Update credits based on selected content type
      const tabCredits = $(this).data("credits");
      if (tabCredits !== undefined) {
        modal
          .find(".content-generator-submit")
          .attr("data-credits-amount", tabCredits);
        updateContentGeneratorCredits(modal);
      }
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-pose-options-single-item`,
    function (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const modal = $(this).closest(modalSelector);
      const textarea = modal.find('textarea[name="prompt"]');
      const promptData = $(this).data("prompt") ?? "";
      const prompts = promptData ? promptData.split("|") : [];

      if (!prompts.length) {
        return;
      }

      const prompt = prompts[Math.floor(Math.random() * prompts.length)];
      textarea.val(prompt).trigger("focus");
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-count-option`,
    function (event) {
      event.preventDefault();
      const modal = $(this).closest(modalSelector);
      modal.find(".content-generator-count-option").removeClass("is-active");
      $(this).addClass("is-active");
      updateContentGeneratorCredits(modal);
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-results-back`,
    function (event) {
      event.preventDefault();
      showContentGeneratorForm($(this).closest(modalSelector));
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-results-trigger`,
    function (event) {
      event.preventDefault();
      showContentGeneratorResults($(this).closest(modalSelector));
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-minimize`,
    function (event) {
      event.preventDefault();
      event.stopPropagation();
      minimizeContentGenerator($(this).closest(modalSelector));
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${contentGeneratorMiniStatusSelector} .content-generator-mini-restore`,
    function (event) {
      event.preventDefault();
      openPersistedContentGeneratorRun();
    }
  );

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${contentGeneratorMiniStatusSelector} .content-generator-mini-close`,
    function () {
      const run = readPersistedContentGeneratorRun();
      if (run?.state === "generating") {
        run.dismissed = true;
        writePersistedContentGeneratorRun(run);
      } else {
        clearPersistedContentGeneratorRun();
      }

      ensureContentGeneratorMiniStatus()
        .data("dismissed", true)
        .addClass("d-none")
        .attr("aria-hidden", "true");
    }
  );

  $(document).on("shown.bs.modal", modalSelector, function () {
    const modal = $(this);
    setContentGeneratorUrlState(true);

    if (modal.data("showResultsOnOpen")) {
      modal.removeData("showResultsOnOpen");
      showContentGeneratorResults(modal);
    }

    ensureContentGeneratorMiniStatus()
      .addClass("d-none")
      .attr("aria-hidden", "true");
  });

  $(document).on("hidden.bs.modal", modalSelector, function () {
    setContentGeneratorUrlState(false);
    const modal = $(this);
    stopAllAudio(modal);

    if (modal.data("generationInProgress")) {
      setContentGeneratorRunState(modal, "generating");
      return;
    }

    const persistedRun = readPersistedContentGeneratorRun();
    if (persistedRun && !persistedRun.dismissed) {
      renderPersistedContentGeneratorRun(persistedRun);
    }

    // Stop async polling if active
    const pollInterval = modal.data("pollInterval");
    if (pollInterval) {
      clearInterval(pollInterval);
      modal.removeData("pollInterval");
    }
    // Stop progress simulation if still running
    const progressInterval = modal.data("progressInterval");
    if (progressInterval) {
      clearInterval(progressInterval);
      modal.removeData("progressInterval");
    }
  });

  contentGeneratorRandom(modalSelector);

  $(document).on(
    "click.growtypeChatContentGenerator",
    `${modalSelector} .content-generator-submit`,
    function (event) {
      event.preventDefault();

      const btn = $(this);
      const modal = btn.closest(modalSelector);
      const errorContainer = modal.find(".content-generator-modal-error");
      const ctaLabel = btn.text();
      const paramsFromModal = modal.attr("data-params");
      let params = {};

      btn.attr("disabled", true);
      errorContainer.addClass("d-none").text("");

      ensureContentGeneratorMiniStatus().data("dismissed", false);
      setContentGeneratorRunState(modal, "generating");
      startContentGeneratorProgressSimulation(modal);

      try {
        params = paramsFromModal ? JSON.parse(paramsFromModal) : {};
      } catch (e) {
        params = {};
      }

      params.reason =
        btn.attr("data-reason") ?? params.reason ?? contentGeneratorReason;
      params.character_slug =
        btn.attr("data-character-slug") ?? params.character_slug ?? "";
      params.character_hash =
        btn.attr("data-character-hash") ?? params.character_hash ?? "";
      params.character_id =
        btn.attr("data-character-id") ?? params.character_id ?? "";
      params.credits_amount =
        params.credits_amount ?? btn.attr("data-credits-amount") ?? "";
      params.prompt = modal.find('textarea[name="prompt"]').val() ?? "";
      params.images_amount =
        modal.find(".content-generator-count-option.is-active").data("value") ??
        1;
      params.content_type =
        modal.find(".content-generator-nav-item.is-active").data("type") ??
        "image";
      persistContentGeneratorRun(params);
      if (params.content_type === "audio") {
        params.tts_model_used =
          modal.find('select[name="content_generator_tts_model_used"]').val() ??
          "";
      } else {
        delete params.tts_model_used;
      }
      params.nsfw = modal.find('input[name="nsfw_chat"]').is(":checked");
      if (document.querySelector("#private[data-type='private']")) {
        params.profile_gallery_context = true;
      } else {
        delete params.profile_gallery_context;
      }

      // Include reference asset URL if set
      var referenceAssetUrl = modal.data("referenceAssetUrl");
      if (referenceAssetUrl) {
        params.reference_asset_url = referenceAssetUrl;
      }

      if (params["cta_label"]) {
        btn.text(params["cta_label"]);
      }

      btn.append('<span class="spinner-border spinner-border-sm ms-2"></span>');

      $.ajax({
        url: growtype_chat_ajax.url,
        type: "post",
        data: {
          action: "growtype_chat_use_credits",
          params: params,
        },
        success: function (response) {
          btn.attr("disabled", false);
          btn.text(ctaLabel);

          if (response.data.existing_credits !== undefined) {
            updateCredits(response.data.existing_credits);
          }

          if (response.success) {
            // ── Async video generation — poll until complete ──
            if (response.data.is_async && response.data.generation_id) {
              // Keep the fast simulation running to 99% while polling. The
              // completed response is the only event that advances it to 100%.
              pollGenerationStatus(
                modal,
                response.data.generation_id,
                btn,
                ctaLabel,
                params,
                errorContainer
              );
              return;
            }

            setContentGeneratorProgress(modal, 100);

            setTimeout(() => {
              stopContentGeneratorProgressSimulation(modal);

              const initialTriggerName = modal.attr(
                "data-initial-trigger-name"
              );

              if (response.data.initial_trigger_text) {
                $(
                  `button[data-bs-target="#growtypeChatUseCreditsModal"][data-initial-trigger-name="${initialTriggerName}"]`
                ).text(response.data.initial_trigger_text);
              }

              if (response.data.generated_content) {
                showGenerationSuccessAlert();

                const resultsList = modal.find(
                  ".content-generator-results-list"
                );
                const generatedData = response.data.generated_content;

                // Handle images/videos
                const generatedVideos = generatedData.videos ?? [];
                const items =
                  generatedVideos.length > 0
                    ? generatedVideos
                    : generatedData.images ?? generatedData.generations ?? [];
                const resultsAreVideos = generatedVideos.length > 0;
                if (items.length) {
                  let html = "";
                  const colClass = "col-4";

                  items.forEach((item) => {
                    const url =
                      typeof item === "string" ? item : item.url ?? "";
                    const id =
                      typeof item === "object"
                        ? item.id ?? item.generation_id ?? ""
                        : "";
                    const preRendered =
                      typeof item === "object" ? item.html ?? "" : "";

                    if (url || preRendered) {
                      html += `<div class="${colClass}"><div class="content-generator-result-item">`;

                      const isVideo =
                        resultsAreVideos ||
                        url.match(/\.(mp4|webm|mov|avi)(\?.*)?$/i);

                      if (isVideo && url) {
                        html += `
                                                  <video controls playsinline preload="metadata" src="${url}" class="img-fluid rounded" style="width:100%;max-height:400px"></video>
                                              `;
                      } else if (preRendered) {
                        html += preRendered;
                      } else {
                        html += `
                                                  <a href="${url}" class="image-wrapper" data-image-id="${id}" data-fancybox="gallery">
                                                      <img src="${url}" class="img-fluid rounded" alt="Generated content">
                                                      <div class="b-actions align-left">
                                                          <div class="icon-useasreference btn-use-as-reference anim-pulse">
                                                              <span class="dashicons dashicons-admin-media"></span>
                                                          </div>
                                                      </div>
                                                  </a>
                                              `;
                      }

                      html += "</div></div>";
                    }
                  });
                  prependContentGeneratorResults(
                    resultsList,
                    resultsAreVideos ? "videos" : "images",
                    html
                  );

                  // Initialize actions for the new results
                  imageWrapperIconImgToVideo(resultsList);
                }

                // Handle audio
                const audioUrls = generatedData.audio_urls ?? [];
                if (audioUrls.length) {
                  let html = "";
                  audioUrls.forEach((url) => {
                    html += `
                                      <div class="col-12">
                                          <div class="content-generator-result-item content-generator-result-audio-item">
                                              <audio controls class="w-100">
                                                  <source src="${url}" type="audio/mpeg">
                                              </audio>
                                          </div>
                                      </div>
                                    `;
                  });
                  prependContentGeneratorResults(resultsList, "audio", html);
                }

                applyContentGeneratorResultsFilter(modal, "all");
                showContentGeneratorResults(modal);
                finishContentGeneratorRun(modal, "ready");

                updateProfileGallery(response.data, params.images_amount);
              } else {
                finishContentGeneratorRun(modal, "ready");
                if (response.data.redirect_url) {
                  const redirectUrl = new URL(
                    response.data.redirect_url,
                    window.location.href
                  );
                  const currentBase =
                    window.location.origin + window.location.pathname;
                  const redirectBase =
                    redirectUrl.origin + redirectUrl.pathname;

                  if (currentBase === redirectBase && redirectUrl.hash) {
                    // Same page — update hash silently (no reload), then do a single reload
                    window.location.href = redirectUrl.href;
                    window.location.reload();
                  } else {
                    window.location.href = response.data.redirect_url;
                  }
                }

                if (response.data.reload) {
                  window.location.reload();
                }

                window.growtypeModal.hideAll();
              }
            }, 500);
          } else {
            stopContentGeneratorProgressSimulation(modal);
            finishContentGeneratorRun(
              modal,
              "error",
              response?.data?.message || "Open to review the error."
            );

            // Server returned success:false over HTTP 200 — wrap it so displayModal can handle it
            const softMessage = response?.data?.message;
            if (softMessage) {
              displayModal({ responseJSON: response });
            }
          }

          document.dispatchEvent(
            useCreditsResponseEvent({
              response: response,
              params: params,
            })
          );
        },
        error: function (response) {
          btn.attr("disabled", false);
          btn.text(ctaLabel);
          stopContentGeneratorProgressSimulation(modal);

          // A navigation or gateway timeout can detach the browser while PHP
          // continues the tracked job. Keep the run live and let the durable
          // status endpoint decide its terminal state.
          if (response.status === 0 || response.status === 504) {
            setContentGeneratorRunState(
              modal,
              "generating",
              "Connection interrupted. Checking generation status."
            );
            startPersistedContentGeneratorPolling();
            return;
          }

          const rawErrorMessage =
            response?.responseJSON?.data?.message ||
            response?.responseJSON?.message;
          let errorMessage = humanizeAccessRequirementMessage(rawErrorMessage);

          if (
            errorMessage ===
            "Character context is missing. Please provide a character slug or reference image."
          ) {
            errorMessage =
              "This image can not be passed as reference image. Please try another image.";
          }

          if (response.status === 500) {
            errorMessage =
              "Generator is taking too long to respond. Please try again in a few moments.";
          }

          if (!errorMessage) {
            errorMessage = "Something went wrong. Please try again.";
          }

          errorContainer.removeClass("d-none").text(errorMessage);
          finishContentGeneratorRun(modal, "error", errorMessage);

          document.dispatchEvent(
            useCreditsResponseEvent({
              response: response,
              params: params,
            })
          );

          // Only use global error handler for known redirects (like credits/auth)
          if (isAccessRequirementError(response?.responseJSON?.data?.message)) {
            errorContainer.addClass("d-none").text("");
            handleAjaxError(response);
          }
        },
        complete: function () {},
      });
    }
  );
}

export function updateContentGeneratorCredits(modal) {
  const submitBtn = modal.find(".content-generator-submit");
  const activeNavItem = modal.find(".content-generator-nav-item.is-active");
  const navCredits = activeNavItem.length
    ? activeNavItem.data("credits")
    : undefined;
  const baseCredits = parseInt(
    navCredits !== undefined
      ? navCredits
      : submitBtn.attr("data-credits-amount") ?? "0",
    10
  );
  const imagesAmount = parseInt(
    modal.find(".content-generator-count-option.is-active").data("value") ??
      "1",
    10
  );
  const totalCredits = Math.max(baseCredits * imagesAmount, 0);

  modal.find(".content-generator-credits-amount").text(totalCredits);
}

export function contentGeneratorRandom(modalSelector) {
  $(document).off("click.growtypeChatContentGeneratorRandom");

  $(document).on(
    "click.growtypeChatContentGeneratorRandom",
    `${modalSelector} .content-generator-random`,
    function (event) {
      event.preventDefault();
      event.stopPropagation();
      const modal = $(this).closest(modalSelector);
      const textarea = modal.find('textarea[name="prompt"]');
      const defaultPrompt = textarea.data("default-prompt") ?? "";
      const prompt = buildRandomPrompt(modal, defaultPrompt);

      textarea.val(prompt).trigger("focus");
    }
  );
}

function buildRandomPrompt(modal, defaultPrompt) {
  const imageGroup = modal.find(
    '.content-generator-pose-options-single[data-type="image"]'
  );
  const groupItems = imageGroup.find(
    ".content-generator-pose-options-single-item"
  );
  let prompt = "";

  if (groupItems.length) {
    const randomItem = groupItems.eq(
      Math.floor(Math.random() * groupItems.length)
    );
    const promptData = randomItem.data("prompt") ?? "";
    const prompts = promptData ? promptData.split("|") : [];
    if (prompts.length) {
      prompt = prompts[Math.floor(Math.random() * prompts.length)];
    }
  }

  if (defaultPrompt && !prompt) {
    prompt = defaultPrompt;
  } else if (defaultPrompt) {
    prompt = `${prompt} ${defaultPrompt}`.trim();
  }

  return prompt.trim();
}

export function setContentGeneratorProgress(container, percent) {
  if (percent >= 100) {
    const progressInterval = container.data("progressInterval");
    if (progressInterval) {
      clearInterval(progressInterval);
      container.removeData("progressInterval");
    }
  }

  ensureContentGeneratingLoader(container);
  const overlay = container.find(
    ".content-generator-modal-loading-overlay, .g-chatform-content-generating-loader"
  );
  const progressBar = overlay.find(".progress-bar");
  const percentText = overlay.find(".percent");

  const circumference = 283;
  const offset = circumference - (percent / 100) * circumference;

  progressBar.css("stroke-dashoffset", offset);
  percentText.text(`${Math.round(percent)}%`);

  const miniStatus = ensureContentGeneratorMiniStatus();
  const miniCircumference = 107;
  const miniOffset = miniCircumference - (percent / 100) * miniCircumference;
  miniStatus
    .find(".content-generator-mini-progress-bar")
    .css("stroke-dashoffset", miniOffset);
  miniStatus
    .find(".content-generator-mini-progress-value")
    .text(`${Math.round(percent)}%`);
}

function showContentGeneratorProgress(container, percent) {
  ensureContentGeneratingLoader(container);
  container
    .find(
      ".content-generator-modal-loading-overlay, .g-chatform-content-generating-loader"
    )
    .removeClass("d-none");
  setContentGeneratorProgress(container, percent);
}

export function startContentGeneratorProgressSimulation(
  container,
  startedAt = Date.now()
) {
  const initialProgress = 0;
  const tickIntervalMs = 100;
  const safeStartedAt = Number(startedAt) || Date.now();
  let progress = initialProgress;
  const initialElapsedMs = Math.max(0, Date.now() - safeStartedAt);
  progress = calculateContentGeneratorProgress(initialElapsedMs);
  showContentGeneratorProgress(container, progress);

  let progressInterval = container.data("progressInterval");
  if (progressInterval) {
    clearInterval(progressInterval);
  }

  progressInterval = setInterval(() => {
    const elapsedMs = Date.now() - safeStartedAt;
    progress = calculateContentGeneratorProgress(elapsedMs);

    if (elapsedMs >= contentGeneratorTimelineDurationMs) {
      progress = contentGeneratorWaitingPercent;
      clearInterval(progressInterval);
      container.removeData("progressInterval");
    }

    setContentGeneratorProgress(container, progress);
  }, tickIntervalMs);

  container.data("progressInterval", progressInterval);
}

export function stopContentGeneratorProgressSimulation(container) {
  let progressInterval = container.data("progressInterval");
  if (progressInterval) {
    clearInterval(progressInterval);
    container.removeData("progressInterval");
  }
  ensureContentGeneratingLoader(container);
  const overlay = container.find(
    ".content-generator-modal-loading-overlay, .g-chatform-content-generating-loader"
  );
  overlay.addClass("d-none");
}

function getGenerationAlertPreference() {
  try {
    return window.localStorage?.getItem(
      "growtype_chat_hide_generation_success_alert"
    );
  } catch (error) {
    return null;
  }
}

function setGenerationAlertPreference() {
  try {
    window.localStorage?.setItem(
      "growtype_chat_hide_generation_success_alert",
      "true"
    );
  } catch (error) {
    // Storage is optional in restricted browser contexts.
  }
}

export function showGenerationSuccessAlert(customText = "") {
  if (getGenerationAlertPreference()) {
    return;
  }

  let alertContainer = $(".growtype-chat-alerts-container");
  if (
    alertContainer.length &&
    alertContainer.find(".growtype-chat-generation-alert").length
  ) {
    return;
  }

  if (!alertContainer.length) {
    alertContainer = $(
      '<div class="growtype-chat-alerts-container alert-position-top"></div>'
    ).appendTo("body");
  }

  const alertHtml = `
        <div class="alert alert-success alert-dismissible fade show shadow-lg growtype-chat-generation-alert" role="alert">
            <div class="alert-content">
                <div class="alert-text">
                    ${
                      customText ||
                      'You can find all generated content in <a href="/assistants/content/" class="alert-link">custom content page</a>.'
                    }
                </div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
    `;

  const $alert = $(alertHtml).appendTo(alertContainer);

  $alert.find(".alert-link").on("click", function () {
    window.growtypeModal.hideAll();

    if (typeof window.growtypeChatCloseGenerateContent === "function") {
      window.growtypeChatCloseGenerateContent();
    }
  });

  $alert.on("close.bs.alert", function () {
    setGenerationAlertPreference();
  });
}

/**
 * Poll the server for async generation completion.
 * Updates the progress bar and renders the result when complete.
 */
function pollGenerationStatus(
  modal,
  generationId,
  btn,
  ctaLabel,
  params,
  errorContainer
) {
  const maxAttempts = 60; // 60 * 3s = 3 minutes
  let attempts = 0;
  let pollTimer = null;
  let pollRequestInFlight = false;
  let completionHandled = false;

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    modal.removeData("pollInterval");
  }

  pollTimer = setInterval(() => {
    if (pollRequestInFlight || completionHandled) {
      return;
    }

    attempts++;
    pollRequestInFlight = true;

    $.ajax({
      url: growtype_chat_ajax.url,
      type: "post",
      data: {
        action: growtype_chat_ajax.check_generation_status,
        generation_id: generationId,
        nonce: growtype_chat_ajax.nonce,
      },
      success: function (statusResponse) {
        if (statusResponse.success && statusResponse.data) {
          const statusData = statusResponse.data;

          if (statusData.status === "completed") {
            if (completionHandled) {
              return;
            }
            completionHandled = true;
            stopPolling();
            setContentGeneratorProgress(modal, 100);

            setTimeout(() => {
              // Re-query the modal in case the DOM has been refreshed
              // since pollGenerationStatus was first called.
              const freshModal = $(contentGeneratorModalSelector);
              if (!freshModal.length) return;

              stopContentGeneratorProgressSimulation(freshModal);

              showGenerationSuccessAlert();

              const resultsList = freshModal.find(
                ".content-generator-results-list"
              );

              // Render video results
              const videoUrls = statusData.video_urls ?? [];
              if (videoUrls.length) {
                let html = "";
                const colClass = "col-4";

                videoUrls.forEach((video) => {
                  const url = video.url ?? "";
                  const preRendered = video.html ?? "";

                  if (url || preRendered) {
                    html +=
                      '<div class="' +
                      colClass +
                      '"><div class="content-generator-result-item">';

                    if (url) {
                      html +=
                        '<video controls playsinline preload="metadata" src="' +
                        url +
                        '" class="img-fluid rounded" style="width:100%;max-height:400px"></video>';
                    } else if (preRendered) {
                      html += preRendered;
                    }

                    html += "</div></div>";
                  }
                });
                prependContentGeneratorResults(resultsList, "videos", html);

                if (
                  resultsList.find(".lazy-load").length > 0 &&
                  typeof $.fn.Lazy === "function"
                ) {
                  resultsList.find(".lazy-load").Lazy();
                }
              }

              applyContentGeneratorResultsFilter(freshModal, "all");
              showContentGeneratorResults(freshModal);
              finishContentGeneratorRun(freshModal, "ready");

              updateProfileGallery(
                statusData,
                statusData.video_urls?.length ?? 1
              );
            }, 500);

            document.dispatchEvent(
              useCreditsResponseEvent({
                response: statusResponse,
                params: params,
              })
            );
            return;
          }

          if (statusData.status === "failed" || statusData.status === "error") {
            stopPolling();
            stopContentGeneratorProgressSimulation(modal);
            errorContainer
              .removeClass("d-none")
              .text(statusData.error || "Generation failed. Please try again.");
            finishContentGeneratorRun(
              modal,
              "error",
              statusData.error || "Generation failed. Please try again."
            );
            btn.attr("disabled", false);
            btn.text(ctaLabel);
            return;
          }

          // Still processing: keep the loader at its simulated value (99% once
          // the short animation completes) until the server reports completion.
        }

        if (attempts >= maxAttempts) {
          stopPolling();
          stopContentGeneratorProgressSimulation(modal);
          errorContainer
            .removeClass("d-none")
            .text("Generation timed out. Please try again.");
          finishContentGeneratorRun(
            modal,
            "error",
            "Generation timed out. Please try again."
          );
          btn.attr("disabled", false);
          btn.text(ctaLabel);
        }
      },
      error: function () {
        if (attempts >= maxAttempts) {
          stopPolling();
          stopContentGeneratorProgressSimulation(modal);
          errorContainer
            .removeClass("d-none")
            .text("Generation timed out. Please try again.");
          finishContentGeneratorRun(
            modal,
            "error",
            "Generation timed out. Please try again."
          );
          btn.attr("disabled", false);
          btn.text(ctaLabel);
        }
      },
      complete: function () {
        pollRequestInFlight = false;
      },
    });
  }, 3000);

  modal.data("pollInterval", pollTimer);
}

function stopAllAudio(container) {
  container.find("audio").each(function () {
    this.pause();
    this.currentTime = 0;
  });
}
